import { getBearerToken } from './token-storage';

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:3001';

let authorityProbe: {
  bearerToken: string;
  promise: Promise<SessionAuthorityResult>;
} | null = null;

export type SessionAuthorityResult =
  | { status: 'valid' }
  | { status: 'invalid-session' }
  | { status: 'temporarily-unavailable' };

/**
 * Verifies the exact session encoded in the current bearer without touching the
 * shared refresh cookie. This must remain a read-only request: lifecycle hints
 * are untrusted and cannot be allowed to rotate cookies or select tenants.
 */
export function probeStoredSessionAuthority(): Promise<SessionAuthorityResult> {
  const bearerToken = getBearerToken();
  if (!bearerToken) return Promise.resolve({ status: 'temporarily-unavailable' });
  if (authorityProbe?.bearerToken === bearerToken) return authorityProbe.promise;

  const request = {
    bearerToken,
    promise: Promise.resolve({ status: 'temporarily-unavailable' } as SessionAuthorityResult),
  };
  request.promise = fetch(`${apiBaseUrl}/auth/sessions`, {
    method: 'GET',
    credentials: 'omit',
    headers: { Authorization: `Bearer ${bearerToken}` },
    cache: 'no-store',
  })
    .then((response): SessionAuthorityResult => {
      if (response.ok) return { status: 'valid' };
      if (response.status === 401) return { status: 'invalid-session' };
      return { status: 'temporarily-unavailable' };
    })
    .catch((): SessionAuthorityResult => ({ status: 'temporarily-unavailable' }))
    .finally(() => {
      if (authorityProbe === request) authorityProbe = null;
    });
  authorityProbe = request;
  return request.promise;
}
